// [Rule of thumb]
// - Respect BE schema (name, term, structure)
//   - Recommend ensuring 1-1 mapping
//   - Unless technical limitations, keep it as close as possible
// - Recommend type narrowing if you know more about the data
//   - `string` to `enum`, `uuid`, `date`, etc
//   - `number` to `int32`
// - Prefix `Cks` which stands for `Checkout Service`
// [REASON]
// - This will help decoupling FE from the BE and keep us safe from BE changes
//   - We will transfer it to a more "FE harmonizing" schema in another phase
// [Reference]
// - https://gitlab.tamaratech.co/tamara-backend/backend-services/checkout-service/-/blob/main/documentation/cosmos-api-docs.yaml

import { z } from "zod";
import { calendarDaysSince } from "@/shared/lib/time";
import {
  CheckoutStatus,
  type CheckoutSession,
  PaymentMethod,
  SessionError,
  Currency,
  PaymentResultStatus,
} from "@/types";
import {
  parseCreditDeclineInfo,
  parseCreditDeclineMoney,
} from "@/shared/lib/parse-credit-decline-info";
import { decodeCurrency } from "@/lib/codec";
import COUNTRY_DATA from "@/country-data";
import { logError, logInfo, logWarn } from "@/services/logging/logger";

/** Wire statuses already reported as unmapped in this page load. */
const loggedUnmappedStatuses = new Set<string>();

const CHECKOUT_STATUS_ENCODED = new Map<string, CheckoutStatus>([
  ["UNKNOWN", CheckoutStatus.UNKNOWN],
  ["ACTIVE", CheckoutStatus.ACTIVE],
  ["NEW", CheckoutStatus.NEW],
  [
    "CHECKING_QUESTIONNAIRE_REQUIRED",
    CheckoutStatus.CHECKING_QUESTIONNAIRE_REQUIRED,
  ],
  ["CHECKING_OVERDUE_PAYMENTS", CheckoutStatus.CHECKING_OVERDUE_PAYMENTS],
  [
    "AWAIT_QUESTIONNAIRE_COMPLETION",
    CheckoutStatus.AWAIT_QUESTIONNAIRE_COMPLETION,
  ],
  [
    "VERIFYING_QUESTIONNAIRE_RESULT",
    CheckoutStatus.VERIFYING_QUESTIONNAIRE_RESULT,
  ],
  [
    "PRE_UNDERWRITING_FRAUD_CHECK",
    CheckoutStatus.PRE_UNDERWRITING_FRAUD_CHECK,
  ],
  // Misspelling still emitted by some backends; must match wire format.
  [
    "PRE_UDNERWRITING_FRAUD_CHECK",
    CheckoutStatus.PRE_UDNERWRITING_FRAUD_CHECK,
  ],
  ["EVALUATING_ORDER_CREDIT", CheckoutStatus.EVALUATING_ORDER_CREDIT],
  ["ORDER_CREDIT_EVALUATED", CheckoutStatus.ORDER_CREDIT_EVALUATED],
  [
    "AWAIT_PAYMENT_OPTION_SELECTION",
    CheckoutStatus.AWAIT_PAYMENT_OPTION_SELECTION,
  ],
  [
    "AWAIT_PAYMENT_METHOD_SELECTION",
    CheckoutStatus.AWAIT_PAYMENT_METHOD_SELECTION,
  ],
  [
    "VALIDATING_PAYMENT_OPTION_SELECTION",
    CheckoutStatus.VALIDATING_PAYMENT_OPTION_SELECTION,
  ],
  ["PAYMENT_OPTION_SELECTED", CheckoutStatus.PAYMENT_OPTION_SELECTED],
  ["PAYMENT_OPTION_VALIDATED", CheckoutStatus.PAYMENT_OPTION_VALIDATED],
  // Purchase-financing loan creation, between PAYMENT_OPTION_VALIDATED and
  // FETCHING_DOCUMENTS. In the API contract since Aug 2025 but never mapped:
  // the plan-screen poll landed here on every single-page BNPL+ session,
  // decoded it as UNKNOWN, stopped polling and painted the plan screen.
  ["CREATING_LOAN", CheckoutStatus.CREATING_LOAN],
  ["LOAN_CREATED", CheckoutStatus.LOAN_CREATED],
  [
    "VALIDATING_PAYMENT_METHOD_SELECTION",
    CheckoutStatus.VALIDATING_PAYMENT_METHOD_SELECTION,
  ],
  ["PAYMENT_METHOD_SELECTED", CheckoutStatus.PAYMENT_METHOD_SELECTED],
  ["AWAIT_IVR_CONFIRMATION", CheckoutStatus.AWAIT_IVR_CONFIRMATION],
  ["IVR_CONFIRMED", CheckoutStatus.IVR_CONFIRMED],
  ["AWAIT_DOCUMENT_SIGNING", CheckoutStatus.AWAIT_DOCUMENT_SIGNING],
  ["FETCHING_DOCUMENTS", CheckoutStatus.FETCHING_DOCUMENTS],
  ["APPROVING_CONTRACT", CheckoutStatus.APPROVING_CONTRACT],
  ["CHECKING_FRAUD", CheckoutStatus.CHECKING_FRAUD],
  ["SCREENING_CHECK", CheckoutStatus.SCREENING_CHECK],
  ["BASIC_FRAUD_CHECK", CheckoutStatus.BASIC_FRAUD_CHECK],
  ["SECOND_FRAUD_CHECK", CheckoutStatus.SECOND_FRAUD_CHECK],
  [
    "PROCESSING_SECOND_FRAUD_CHECK",
    CheckoutStatus.PROCESSING_SECOND_FRAUD_CHECK,
  ],
  ["AWAIT_BANK_INFO_STEP", CheckoutStatus.AWAIT_BANK_INFO_STEP],
  ["THIRD_FRAUD_CHECK", CheckoutStatus.THIRD_FRAUD_CHECK],
  ["PROCESSING_THIRD_FRAUD_CHECK", CheckoutStatus.PROCESSING_THIRD_FRAUD_CHECK],
  ["RESERVE_DOWNPAYMENT_BALANCE", CheckoutStatus.RESERVE_DOWNPAYMENT_BALANCE],
  ["INITIALIZE_CARD_PAYMENT", CheckoutStatus.INITIALIZE_CARD_PAYMENT],
  ["PROCESSING_CARD_PAYMENT", CheckoutStatus.PROCESSING_CARD_PAYMENT],
  ["CARD_PROCESSING_SUCCEEDED", CheckoutStatus.CARD_PROCESSING_SUCCEEDED],
  // Set for ~50ms when a card is declined, before the BE returns the session to
  // AWAIT_PAYMENT_METHOD_SELECTION. Declared in CheckoutStatus since Jan 2026
  // (CXO-1885) but never mapped here, so any poll landing in that window failed
  // to parse and killed the page.
  ["CARD_PROCESSING_FAILED", CheckoutStatus.CARD_PROCESSING_FAILED],
  [
    "AWAIT_EARLY_REPAYMENT_COMPLETION",
    CheckoutStatus.AWAIT_EARLY_REPYAYMENT_COMPLETION,
  ],
  // FDS prevention flow — backend typo on REPYAYMENT is intentional and must
  // match the wire format byte-for-byte. Do not "fix" it on the FE.
  [
    "AWAIT_EARLY_REPYAYMENT_COMPLETION",
    CheckoutStatus.AWAIT_EARLY_REPYAYMENT_COMPLETION,
  ],
  [
    "VALIDATING_EARLY_REPAYMENT_PAYMENT",
    CheckoutStatus.VALIDATING_EARLY_REPAYMENT_PAYMENT,
  ],
  [
    "EARLY_REPAYMENT_COMPLETED",
    CheckoutStatus.EARLY_REPAYMENT_COMPLETED,
  ],
  ["AWAIT_QUESTIONNAIRE_COMPLETION", CheckoutStatus.AWAIT_QUESTIONNAIRE_COMPLETION],
  ["CHECKING_IDV_ELIGIBILITY", CheckoutStatus.CHECKING_IDV_ELIGIBILITY],
  ["AWAIT_IDV_COMPLETION", CheckoutStatus.AWAIT_IDV_COMPLETION],
  ["VERIFYING_IDV_RESULT", CheckoutStatus.VERIFYING_IDV_RESULT],
  ["FINISHED", CheckoutStatus.FINISHED],
  ["SUCCEEDED", CheckoutStatus.SUCCEEDED],
  ["FAILED", CheckoutStatus.FAILED],
  ["INVALIDATED", CheckoutStatus.INVALIDATED],
]);

export const CksMoneySchema = z
  .object({
    amount: z.number().catch(0),
    currency: z.string().catch(""),
  })
  .catch({
    amount: 0,
    currency: "",
  });
export type CksMoney = z.infer<typeof CksMoneySchema>;

export const CksPaymentTypeSchema = z.enum([
  "PAY_BY_INSTALMENTS",
  "PAY_NOW",
  "PAY_NEXT_MONTH",
  "PAY_BY_LATER",
  "PAY_BY_FINANCE",
]);
export type CksPaymentType = z.infer<typeof CksPaymentTypeSchema>;

// Deliberately permissive: the backend state machine transitions through
// statuses the FE does not know about (transient ones especially). Validating
// against a fixed allow-list made every unlisted status fatal — the whole
// session failed to parse and the page died. Unrecognised values now fall
// through to the CheckoutStatus.UNKNOWN mapping below.
// `.catch` covers the non-string case too (a status must never be fatal).
export const CksStatus = z.string().catch("UNKNOWN");
export type CksStatus = z.infer<typeof CksStatus>;

export const CksPaymentMethodSchema = z.enum([
  "KNET_CARD",
  "APPLE_PAY",
  "CREDIT_CARD",
  "MADA_CARD",
  "TAMARA_PAY_CARD",
]);
export type CksPaymentMethod = z.infer<typeof CksPaymentMethodSchema>;

const CksPaymentResultErrorItemSchema = z.object({
  code: z.string().optional().nullable(),
  message: z.string().optional().nullable(),
});

export const CksPaymentResultSchema = z.object({
  redirectUrl: z.string().optional().nullable(),
  errorMessage: z.string().optional().nullable(),
  errorCode: z.string().optional().nullable(),
  status: z.string().optional().nullable(),
  errors: z
    .array(CksPaymentResultErrorItemSchema)
    .optional()
    .nullable()
    .catch([]),
});
export type CksPaymentResult = z.infer<typeof CksPaymentResultSchema>;

export const CksMerchantSchema = z.object({
  id: z.string(),
  name: z.string(),
  arName: z.string(),
  platform: z.string(),
  logoUrl: z.string().catch(""),
  // [TODO] fulfill more fields
});
export type CksMerchant = z.infer<typeof CksMerchantSchema>;

export const CksMerchantUrlsSchema = z.object({
  success: z.string(),
  failure: z.string(),
  cancel: z.string(),
});
export type CksMerchantUrls = z.infer<typeof CksMerchantUrlsSchema>;

export const CksOrderItemSchema = z.object({
  referenceId: z.string(),
  type: z.string(),
  name: z.string(),
  sku: z.string(),
  quantity: z.int32(),
  taxAmount: CksMoneySchema,
  totalAmount: CksMoneySchema,
  unitPrice: CksMoneySchema,
  discountAmount: CksMoneySchema,
  imageUrl: z.string().default("").catch(""),
  itemUrl: z.string().default("").catch(""),
});
export type CksOrderItem = z.infer<typeof CksOrderItemSchema>;

export const CksOrderSchema = z.object({
  id: z.string(),
  countryCode: z.string(),
  items: z.array(CksOrderItemSchema).catch([]),
});
export type CksOrder = z.infer<typeof CksOrderSchema>;

export const CksSchedulePreviewSchema = z.object({
  sequence: z.number(),
  dueNow: z.boolean(),
  dueDate: z.string(),
  amount: CksMoneySchema,
});
export type CksSchedulePreview = z.infer<typeof CksSchedulePreviewSchema>;

export const CksPaymentOptionSchema = z.object({
  offerId: z.uuid(),
  tenure: z.int32(),
  productType: z.string().nullable().optional(),
  downPaymentAmount: CksMoneySchema,
  // proccesingFeeAmount: CksMoneySchema,
  processingFeeAmount: CksMoneySchema,
  instalmentAmount: CksMoneySchema,
  // principleAmount: CksMoneySchema,
  principalAmount: CksMoneySchema,
  termCostAmount: CksMoneySchema,
  totalToBeRepaidAmount: CksMoneySchema,
  totalCustomerFeeAmount: CksMoneySchema,
  averageMonthlyFee: CksMoneySchema.optional(),
  schedulePreview: z.array(CksSchedulePreviewSchema),
  needsDocumentSigning: z.boolean(),
  // Accept rich cashback object from BE; keep flexible for now
  cashback: z.any().nullable(),
  appliedRewards: z.any().nullable().optional(),
  subvention: z.any().nullable().optional(),
  equalDownPaymentStrategy: z.boolean().nullable(),
  preSelected: z.boolean().nullable().optional(),
  waivedOffFee: CksMoneySchema.nullable().optional(),
  waivedOffReason: z.string().nullable().optional(),
  isFeeWaivedOff: z.boolean().nullable().optional(),
  annualPercentageRate: z.number().nullable().optional(),
  firstRepaymentDate: z.string().nullable().optional(),
  traits: z.array(z.string()).nullable().optional(),
});
export type CksPaymentOption = z.infer<typeof CksPaymentOptionSchema>;

export const CksLoanDocumentSchema = z.object({
  id: z.string(),
  documentUrl: z.string().nullable().optional(),
  documentType: z.string().nullable().catch(""),
  attributes: z.array(z.string()).nullable().optional(),
});
export type CksLoanDocument = z.infer<typeof CksLoanDocumentSchema>;

/**
 * Early repayment payload — present when status is
 * AWAIT_EARLY_REPYAYMENT_COMPLETION (FDS prevention flow).
 * Field names are camelCase because the axios response interceptor camelizes
 * the BE snake_case payload before it reaches this schema.
 */
const mapEarlyRepaymentFields = (
  er: Record<string, unknown> | null | undefined,
) => {
  if (!er) return null;
  const raw = er as Record<string, unknown>;
  const logo =
    (raw.merchantLogoUrl as string | undefined) ??
    (raw.merchant_logo_url as string | undefined) ??
    (raw.merchantLogo as string | undefined) ??
    (raw.merchant_logo as string | undefined) ??
    "";
  const amountRaw =
    (raw.amount as { amount?: number; currency?: string } | undefined) ??
    (raw.totalAmount as { amount?: number; currency?: string } | undefined) ??
    (raw.total_amount as { amount?: number; currency?: string } | undefined) ??
    (raw.dueAmount as { amount?: number; currency?: string } | undefined) ??
    (raw.due_amount as { amount?: number; currency?: string } | undefined);
  const daysOverdueRaw = raw.daysOverdue ?? raw.days_overdue;
  const daysOverdue =
    typeof daysOverdueRaw === "number" && Number.isFinite(daysOverdueRaw)
      ? daysOverdueRaw
      : undefined;
  const installmentIndexRaw = raw.installmentIndex ?? raw.installment_index;
  const installmentIndex =
    typeof installmentIndexRaw === "number" &&
    Number.isFinite(installmentIndexRaw)
      ? installmentIndexRaw
      : 0;

  const orderIdRaw =
    raw.orderId ??
    raw.order_id ??
    (Array.isArray(raw.orderIds)
      ? raw.orderIds[0]
      : Array.isArray(raw.order_ids)
        ? raw.order_ids[0]
        : null);

  return {
    paymentId: String(raw.paymentId ?? raw.payment_id ?? ""),
    amount: {
      amount: amountRaw?.amount ?? 0,
      currency: amountRaw?.currency ?? "",
    },
    orderId: orderIdRaw != null ? String(orderIdRaw) : "",
    merchantId: String(raw.merchantId ?? raw.merchant_id ?? ""),
    merchantName: String(raw.merchantName ?? raw.merchant_name ?? ""),
    merchantLogoUrl: logo,
    orderReference: String(
      raw.orderReference ?? raw.order_reference ?? raw.orderReferenceId ?? "",
    ),
    dueDate: String(raw.dueDate ?? raw.due_date ?? ""),
    installmentIndex,
    ...(daysOverdue !== undefined ? { daysOverdue } : {}),
  };
};

export const CksEarlyRepaymentSchema = z
  .record(z.string(), z.unknown())
  .transform((raw) => mapEarlyRepaymentFields(raw));
export type CksEarlyRepayment = NonNullable<
  ReturnType<typeof mapEarlyRepaymentFields>
>;

export const CksOverduePaymentsSchema = z
  .record(z.string(), z.unknown())
  .transform((raw) => {
    const data = raw as Record<string, unknown>;
    const totalDueRaw =
      (data.totalDueAmount as { amount?: number; currency?: string } | undefined) ??
      (data.total_due_amount as { amount?: number; currency?: string } | undefined) ??
      (data.total as { amount?: number; currency?: string } | undefined);
    const paymentsRaw =
      (data.payments as unknown[] | undefined) ??
      (data.repayments as unknown[] | undefined) ??
      [];
    const payments = paymentsRaw
      .map((item) =>
        mapEarlyRepaymentFields(
          item as Record<string, unknown> | null | undefined,
        ),
      )
      .filter((item): item is CksEarlyRepayment => item != null);

    const hasOverdueRaw = data.hasOverdue ?? data.has_overdue;
    const maxDaysOverdueRaw = data.maxDaysOverdue ?? data.max_days_overdue;
    const countRaw = data.count;
    const maxDaysOverdue =
      typeof maxDaysOverdueRaw === "number" && Number.isFinite(maxDaysOverdueRaw)
        ? maxDaysOverdueRaw
        : 0;

    const paymentsWithDaysOverdue = payments.map((payment) => {
      if (payment.daysOverdue != null && payment.daysOverdue > 0) {
        return payment;
      }
      if (!payment.dueDate) {
        return maxDaysOverdue > 0
          ? { ...payment, daysOverdue: maxDaysOverdue }
          : payment;
      }
      const days = calendarDaysSince(payment.dueDate);
      if (days != null && days > 0) return { ...payment, daysOverdue: days };
      return maxDaysOverdue > 0
        ? { ...payment, daysOverdue: maxDaysOverdue }
        : payment;
    });

    return {
      hasOverdue:
        typeof hasOverdueRaw === "boolean" ? hasOverdueRaw : payments.length > 0,
      maxDaysOverdue,
      totalDueAmount: {
        amount: totalDueRaw?.amount ?? 0,
        currency: totalDueRaw?.currency ?? "",
      },
      count:
        typeof countRaw === "number" && Number.isFinite(countRaw)
          ? countRaw
          : payments.length,
      payments: paymentsWithDaysOverdue,
    };
  });
export type CksOverduePayments = z.infer<typeof CksOverduePaymentsSchema>;

/** Accept BE snake_case or client camelCase (axios camelizes response); normalize to camelCase. */
const DeclinedOfferSubventionRawSchema = z
  .record(z.string(), z.unknown())
  .transform((d) => {
    const s = d as Record<string, unknown>;
    return {
      applied: s.applied as boolean | undefined,
      campaignId: (s.campaign_id ?? s.campaignId) as string | undefined,
      rejectionCode: (s.rejection_code ?? s.rejectionCode) as
        | string
        | undefined,
      subventionBreakdown: (s.subvention_breakdown ?? s.subventionBreakdown) as
        | Record<string, unknown>
        | undefined,
      bySkuBreakdown: (s.by_sku_breakdown ?? s.bySkuBreakdown) as
        | Record<string, unknown>[]
        | undefined,
      campaignBreakdown: (s.campaign_breakdown ?? s.campaignBreakdown) as
        | Record<string, unknown>[]
        | undefined,
    };
  });

/** Parsed subvention shape from declined_offers[].subvention. */
export const CksDeclinedOfferSubventionSchema =
  DeclinedOfferSubventionRawSchema;

/** Parse declined_offers item (snake_case from BE or camelCase after axios); normalize to DeclinedOffer. */
export const CksDeclinedOfferSchema = z
  .record(z.string(), z.unknown())
  .transform((d) => {
    const raw = d as Record<string, unknown>;
    const subventionRaw = raw.subvention;
    const isSubventionObj =
      subventionRaw != null && typeof subventionRaw === "object";
    const subventionObj = isSubventionObj
      ? (subventionRaw as Record<string, unknown>)
      : null;
    const parsedSubvention = isSubventionObj
      ? DeclinedOfferSubventionRawSchema.safeParse(subventionRaw)
      : { success: false as const, data: null };
    const offerIdRaw =
      raw.offer_id ??
      raw.offerId ??
      subventionObj?.offer_id ??
      subventionObj?.offerId;
    const offerId = offerIdRaw != null ? String(offerIdRaw) : "";
    const tenure =
      typeof raw.tenure === "number"
        ? raw.tenure
        : typeof raw.tenure === "string"
          ? Number(raw.tenure)
          : 0;
    return {
      orderId: (raw.order_id ?? raw.orderId) as string | undefined,
      customerId: (raw.customer_id ?? raw.customerId) as string | undefined,
      merchantId: (raw.merchant_id ?? raw.merchantId) as string | undefined,
      offerId,
      reason: raw.reason as string | undefined,
      tenure: Number.isFinite(tenure) ? tenure : 0,
      subvention: parsedSubvention.success ? parsedSubvention.data : null,
    };
  });
export type CksDeclinedOffer = z.infer<typeof CksDeclinedOfferSchema>;

const CksCreditDeclineInfoRawSchema = z
  .record(z.string(), z.unknown())
  .nullable()
  .optional();

const CksErrorDetailsSchema = z
  .object({
    errorCode: z.string().nullable().optional(),
    errorDetail: z.string().nullable().optional(),
    errorMessage: z.string().nullable().optional(),
    errorTitle: z.string().nullable().optional(),
    // Support snake_case from API in case response is not camelized
    error_code: z.string().nullable().optional(),
    error_detail: z.string().nullable().optional(),
    error_message: z.string().nullable().optional(),
    error_title: z.string().nullable().optional(),
  })
  .nullable()
  .optional();

const resolveSessionErrorFromCheckoutPayload = (
  data: {
    errorDetails?: {
      errorCode?: string | null;
      errorMessage?: string | null;
      errorTitle?: string | null;
      error_code?: string | null;
      error_message?: string | null;
      error_title?: string | null;
    } | null;
    error_details?: {
      errorCode?: string | null;
      errorMessage?: string | null;
      errorTitle?: string | null;
      error_code?: string | null;
      error_message?: string | null;
      error_title?: string | null;
    } | null;
    errorMessage?: string | null;
    errorCode?: string | null;
  },
  status: CheckoutStatus,
  creditDeclineInfo: ReturnType<typeof parseCreditDeclineInfo>,
): {
  type: SessionError;
  message: string;
  title?: string;
  code?: string;
} => {
  // Hard rule: when BE sends both error_details and credit_decline_info (e.g.
  // merchant disabled PiF), show Decline UI — not the generic ErrorScreen.
  // Requested by Abanoub (BE), CXO-2657 — Slack C0B1LBWJ63X.
  if (creditDeclineInfo) {
    return {
      type: SessionError.NONE,
      message: "",
    };
  }

  const errorDetails = data.errorDetails ?? data.error_details;
  const errorMessage = String(
    errorDetails?.errorMessage ??
      errorDetails?.error_message ??
      data.errorMessage ??
      "",
  );
  const errorTitle = String(
    errorDetails?.errorTitle ?? errorDetails?.error_title ?? "",
  );
  const errorCode = String(
    errorDetails?.errorCode ?? errorDetails?.error_code ?? data.errorCode ?? "",
  );

  if (errorMessage && errorMessage !== "") {
    return {
      type: SessionError.UNKNOWN,
      message: errorMessage,
      ...(errorTitle ? { title: errorTitle } : {}),
      ...(errorCode ? { code: errorCode } : {}),
    };
  }

  if (
    status === CheckoutStatus.FAILED ||
    status === CheckoutStatus.INVALIDATED
  ) {
    return {
      type: SessionError.UNKNOWN,
      message: "",
      ...(errorTitle ? { title: errorTitle } : {}),
      ...(errorCode ? { code: errorCode } : {}),
    };
  }

  return {
    type: SessionError.NONE,
    message: "",
  };
};

type CheckoutDeclineAndErrorPayload = {
  creditDeclineInfo?: unknown;
  credit_decline_info?: unknown;
  errorDetails?: unknown;
  error_details?: unknown;
};

function asCheckoutPayloadRecord(
  value: unknown,
): Record<string, unknown> | null {
  if (!value || typeof value !== "object") {
    return null;
  }

  return value as Record<string, unknown>;
}

function summarizeRawCreditDeclineInfo(raw: unknown) {
  if (raw == null) {
    return { is_null: true };
  }

  const data = asCheckoutPayloadRecord(raw);
  if (!data) {
    return { is_null: false, is_object: false };
  }

  const explainer = asCheckoutPayloadRecord(
    data.creditDeclineExplainer ??
      data.credit_decline_explainer ??
      data.declineExplainer ??
      data.decline_explainer,
  );

  return {
    is_null: false,
    top_level_keys: Object.keys(data),
    credit_decline_code:
      data.creditDeclineCode ??
      data.credit_decline_code ??
      data.declineCode ??
      data.decline_code ??
      null,
    has_credit_decline_explainer: explainer != null,
    explainer_keys: explainer ? Object.keys(explainer) : [],
  };
}

function summarizeParsedCreditDeclineInfo(
  parsed: ReturnType<typeof parseCreditDeclineInfo>,
) {
  if (!parsed) {
    return { is_null: true };
  }

  return {
    is_null: false,
    decline_code: parsed.declineCode,
    breakdown_types: parsed.breakdown.map((item) => item.type),
    has_order_amount: parsed.orderAmount != null,
    has_recommended_limit: parsed.recommendedLimit != null,
  };
}

function summarizeErrorDetails(raw: unknown) {
  if (raw == null) {
    return { is_null: true };
  }

  const data = asCheckoutPayloadRecord(raw);
  if (!data) {
    return { is_null: false, is_object: false };
  }

  const errorMessage = data.errorMessage ?? data.error_message;

  return {
    is_null: false,
    error_code: data.errorCode ?? data.error_code ?? null,
    error_title: data.errorTitle ?? data.error_title ?? null,
    has_error_message:
      typeof errorMessage === "string" && errorMessage.trim() !== "",
  };
}

function logCreditDeclineInfoAndErrorDetails(
  sessionId: string | null | undefined,
  data: CheckoutDeclineAndErrorPayload,
  parsedCreditDeclineInfo: ReturnType<typeof parseCreditDeclineInfo>,
): void {
  const rawCreditDeclineInfo =
    data.creditDeclineInfo ?? data.credit_decline_info ?? null;
  const rawErrorDetails = data.errorDetails ?? data.error_details ?? null;
  const hasParsedCreditDeclineInfo = parsedCreditDeclineInfo != null;

  logInfo(
    "checkout-session",
    "Checkout credit_decline_info and error_details received",
    {
      session_id: sessionId ?? null,
      credit_decline_info: summarizeRawCreditDeclineInfo(rawCreditDeclineInfo),
      credit_decline_info_parsed:
        summarizeParsedCreditDeclineInfo(parsedCreditDeclineInfo),
      has_credit_decline_info: rawCreditDeclineInfo != null,
      has_parsed_credit_decline_info: hasParsedCreditDeclineInfo,
      raw_credit_decline_info_unparsed:
        rawCreditDeclineInfo != null && !hasParsedCreditDeclineInfo,
      error_details: summarizeErrorDetails(rawErrorDetails),
      has_error_details: rawErrorDetails != null,
      error_details_without_credit_decline_info:
        rawErrorDetails != null && !hasParsedCreditDeclineInfo,
    },
  );
};

export const CksCheckoutStateSchema = z
  .object({
    id: z.uuid().nullable().optional(),
    orderId: z.uuid().nullable().optional(),
    orderTotalAmount: CksMoneySchema.nullable().optional(),
    paymentType: CksPaymentTypeSchema.nullable().optional(),
    status: CksStatus.nullable().optional(),
    merchant: CksMerchantSchema.nullable().optional(),
    merchantUrls: CksMerchantUrlsSchema.catch({
      cancel: "",
      failure: "",
      success: "",
    }),
    order: CksOrderSchema.catch({
      id: "",
      countryCode: "",
      items: [],
    }),
    customerId: z.uuid().nullable().optional(),
    paymentOptions: z.array(CksPaymentOptionSchema).nullable().optional(),
    selectedPaymentOptionId: z.string().catch(""),
    /** BE: is_offer_pre_selected (camelized by axios). Drives single page checkout. */
    isOfferPreSelected: z.boolean().nullable().optional(),
    /** BE: should_use_payment_widget (camelized by axios). When true, render the outsourced payment widget instead of PPUI payment methods. */
    shouldUsePaymentWidget: z.boolean().nullable().optional(),
    // Support snake_case from API in case response is not camelized
    should_use_payment_widget: z.boolean().nullable().optional(),
    locale: z.string().nullable().optional(),
    installments: z.int32().nullable().optional(),
    /** Canonical BE field (replaces loan_documents). */
    documents: z.array(CksLoanDocumentSchema).optional(),
    /** @deprecated Prefer `documents`; retained for rollout compatibility. */
    loanDocuments: z.array(CksLoanDocumentSchema).optional(),
    paymentMethod: CksPaymentMethodSchema.nullable().optional(),
    paymentDetails: z
      .object({
        availableWalletBalance: CksMoneySchema.nullable().optional(),
        cardDownPaymentAmountWithWallet: CksMoneySchema.nullable().optional(),
        walletDownPaymentAmount: CksMoneySchema.nullable().optional(),
        canWalletCoverDownPayment: z.boolean().nullable().optional(),
      })
      .nullable()
      .optional(),
    paymentResult: CksPaymentResultSchema.nullable().catch({
      redirectUrl: "",
    }),
    errorCode: z.string().nullable().optional(),
    errorMessage: z.string().nullable().optional(),
    errorDetails: CksErrorDetailsSchema,
    error_details: CksErrorDetailsSchema,
    shouldShowIvr: z.boolean().nullable().optional(),
    shouldShowDocumentSigning: z.boolean().nullable().optional(),
    ivrStatus: z.string().nullable().optional(),
    ivrTimeout: z.number().nullable().optional(),
    /** Set once the BE has actually placed an IVR call for this checkout. */
    triggeredIvrAt: z.string().nullable().optional(),
    declinedOffers: z.array(CksDeclinedOfferSchema).optional().catch([]),
    // Support snake_case from API in case response is not camelized
    declined_offers: z.array(CksDeclinedOfferSchema).optional().catch([]),
    earlyRepayment: CksEarlyRepaymentSchema.nullable().optional(),
    // Support snake_case from API in case response is not camelized
    early_repayment: CksEarlyRepaymentSchema.nullable().optional(),
    overduePayments: CksOverduePaymentsSchema.nullable().optional(),
    overdue_payments: CksOverduePaymentsSchema.nullable().optional(),
    redirectUrl: z.string().optional().nullable(),
    redirect_url: z.string().optional().nullable(),
    redirectionUrl: z.string().optional().nullable(),
    redirection_url: z.string().optional().nullable(),
    creditDeclineInfo: CksCreditDeclineInfoRawSchema,
    credit_decline_info: CksCreditDeclineInfoRawSchema,
  })
  .transform((data): CheckoutSession => {
    const defaultCountry = COUNTRY_DATA[0];
    const countryFound = COUNTRY_DATA.find((country) =>
      [country.isoCode2, country.isoCode3].includes(data.order.countryCode),
    );
    if (!countryFound) {
      logError(
        "checkout-service",
        `Failed to resolve country! Defaulting to ${defaultCountry.isoCode2}`,
        undefined,
        { countryCode: data.order.countryCode },
      );
    }

    const rawStatus = data?.status ?? "";
    const status =
      CHECKOUT_STATUS_ENCODED.get(rawStatus) ?? CheckoutStatus.UNKNOWN;
    if (
      status === CheckoutStatus.UNKNOWN &&
      rawStatus !== "" &&
      rawStatus !== "UNKNOWN" &&
      !loggedUnmappedStatuses.has(rawStatus)
    ) {
      // Once per wire value per page load: the UNKNOWN fallback is silent
      // otherwise, and a missing mapping (CREATING_LOAN, CXO-3599) went
      // unnoticed for months because nothing said which status was dropped.
      loggedUnmappedStatuses.add(rawStatus);
      logWarn(
        "checkout-service",
        "Unrecognised checkout status; decoding as UNKNOWN",
        { status: rawStatus, sessionId: data?.id },
      );
    }

    const creditDeclineInfo = parseCreditDeclineInfo(
      data?.creditDeclineInfo ?? data?.credit_decline_info,
      parseCreditDeclineMoney(data?.orderTotalAmount),
    );

    logCreditDeclineInfoAndErrorDetails(data?.id, data, creditDeclineInfo);

    const error = resolveSessionErrorFromCheckoutPayload(
      data,
      status,
      creditDeclineInfo,
    );

    return {
      id: data?.id ?? "",
      country: countryFound ?? defaultCountry,
      orderId: data?.orderId ?? "",
      orderTotalAmount: {
        amount: data?.orderTotalAmount?.amount ?? 0,
        currency: data?.orderTotalAmount?.currency ?? "",
      },
      paymentType: data?.paymentType ?? "PAY_BY_INSTALMENTS",
      status,
      merchant: {
        id: data?.merchant?.id ?? "",
        name: data?.merchant?.name ?? "",
        arName: data?.merchant?.arName ?? "",
        platform: data?.merchant?.platform ?? "",
        logoUrl: data?.merchant?.logoUrl ?? "",
        urls: {
          success: data?.merchantUrls?.success ?? "",
          failure: data?.merchantUrls?.failure ?? "",
          cancel: data?.merchantUrls?.cancel ?? "",
        },
      },
      order: {
        id: data?.order?.id ?? "",
        countryCode: data?.order?.countryCode ?? "",
        // eslint-disable-next-line @typescript-eslint/no-explicit-any
        items: (data?.order?.items ?? []).map((item: any) => ({
          id: item?.referenceId ?? "",
          referenceId: item?.referenceId ?? "",
          type: item?.type ?? "",
          name: item?.name ?? "",
          sku: item?.sku ?? "",
          quantity: item?.quantity ?? 0,
          taxAmount: {
            amount: item?.taxAmount?.amount ?? 0,
            currency: item?.taxAmount?.currency ?? "",
          },
          totalAmount: {
            amount: item?.totalAmount?.amount ?? 0,
            currency: item?.totalAmount?.currency ?? "",
          },
          unitPrice: {
            amount: item?.unitPrice?.amount ?? 0,
            currency: item?.unitPrice?.currency ?? "",
          },
          discountAmount: {
            amount: item?.discountAmount?.amount ?? 0,
            currency: item?.discountAmount?.currency ?? "",
          },
          imageUrl: item?.imageUrl ?? "",
          itemUrl: item?.itemUrl ?? "",
        })),
        currency:
          decodeCurrency(data?.orderTotalAmount?.currency ?? "") ??
          Currency.SAR,
      },
      customerId: data?.customerId ?? "",
      paymentOptions: (data?.paymentOptions ?? []).map((option) => ({
        ...option,
        subvention: option.subvention ?? null,
        waivedOffFee: option.waivedOffFee ?? null,
        waivedOffReason: option.waivedOffReason ?? null,
        isFeeWaivedOff: option.isFeeWaivedOff ?? false,
      })),
      selectedPaymentOptionId: data?.selectedPaymentOptionId ?? "",
      isOfferPreSelected: data?.isOfferPreSelected ?? false,
      shouldUsePaymentWidget:
        data?.shouldUsePaymentWidget ?? data?.should_use_payment_widget ?? false,
      locale: data?.locale ?? "",
      installments: data?.installments ?? null,
      documents: (() => {
        const merged = [...(data?.documents ?? []), ...(data?.loanDocuments ?? [])];
        const seen = new Set<string>();
        const unique = merged.filter((document) => {
          const id = (document?.id ?? "").trim();
          if (!id || seen.has(id)) return false;
          seen.add(id);
          return true;
        });
        return unique.map((document) => ({
          id: document?.id ?? "",
          documentItemType: document?.documentType ?? "",
          url: document?.documentUrl ?? "",
          attributes:
            document?.attributes === undefined ? null : document.attributes,
        }));
      })(),
      paymentMethod:
        new Map([
          ["KNET_CARD", PaymentMethod.KNET_CARD],
          ["APPLE_PAY", PaymentMethod.APPLE_PAY],
          ["CREDIT_CARD", PaymentMethod.CREDIT_CARD],
          ["MADA_CARD", PaymentMethod.MADA_CARD],
          ["TAMARA_PAY_CARD", PaymentMethod.TAMARA_PAY_CARD],
        ]).get(data?.paymentMethod ?? "") ?? null,
      paymentDetails: data?.paymentDetails
        ? {
            availableWalletBalance:
              data.paymentDetails.availableWalletBalance &&
              data.paymentDetails.availableWalletBalance.amount !== null &&
              data.paymentDetails.availableWalletBalance.amount !== undefined
                ? {
                    amount: data.paymentDetails.availableWalletBalance.amount,
                    currency:
                      data.paymentDetails.availableWalletBalance.currency,
                  }
                : undefined,
            cardDownPaymentAmountWithWallet: data.paymentDetails
              .cardDownPaymentAmountWithWallet
              ? {
                  amount:
                    data.paymentDetails.cardDownPaymentAmountWithWallet.amount,
                  currency:
                    data.paymentDetails.cardDownPaymentAmountWithWallet
                      .currency,
                }
              : undefined,
            walletDownPaymentAmount: data.paymentDetails.walletDownPaymentAmount
              ? {
                  amount: data.paymentDetails.walletDownPaymentAmount.amount,
                  currency:
                    data.paymentDetails.walletDownPaymentAmount.currency,
                }
              : undefined,
            canWalletCoverDownPayment:
              data.paymentDetails.canWalletCoverDownPayment ?? false,
          }
        : undefined,
      error,
      paymentResult: (() => {
        const pr = data?.paymentResult;
        const firstError = Array.isArray(pr?.errors) ? pr.errors[0] : undefined;
        return {
          redirectUrl: pr?.redirectUrl ?? "",
          status:
            new Map<string, PaymentResultStatus>([
              ["declined", PaymentResultStatus.DECLINED],
              ["denied", PaymentResultStatus.DENIED],
              ["pending", PaymentResultStatus.PENDING],
              // Failure-like statuses: always show payment error (use default message if no error_message)
              ["network_error", PaymentResultStatus.DECLINED],
              ["failed", PaymentResultStatus.DECLINED],
              ["error", PaymentResultStatus.DECLINED],
              ["failure", PaymentResultStatus.DECLINED],
            ]).get((pr?.status ?? "").toLowerCase()) ??
            PaymentResultStatus.UNKNOWN,
          errorCode: pr?.errorCode ?? firstError?.code ?? "",
          errorMessage: pr?.errorMessage ?? firstError?.message ?? "",
        };
      })(),
      shouldShowIvr: data?.shouldShowIvr ?? false,
      shouldShowDocumentSigning: data?.shouldShowDocumentSigning ?? false,
      ivrStatus: data?.ivrStatus ?? "",
      ivrTimeout: data?.ivrTimeout ?? 0,
      triggeredIvrAt: data?.triggeredIvrAt ?? null,
      declinedOffers: data?.declinedOffers ?? data?.declined_offers ?? [],
      earlyRepayment: (() => {
        const er = data?.earlyRepayment ?? data?.early_repayment;
        return er ?? null;
      })(),
      overduePayments: (() => {
        const op = data?.overduePayments ?? data?.overdue_payments;
        if (!op) return null;
        const parsed = CksOverduePaymentsSchema.safeParse(op);
        return parsed.success ? parsed.data : null;
      })(),
      redirectUrl:
        data?.redirectUrl ??
        data?.redirect_url ??
        data?.redirectionUrl ??
        data?.redirection_url ??
        "",
      creditDeclineInfo,
    };
  });
export type CksCheckoutState = z.infer<typeof CksCheckoutStateSchema>;
